import React, { useState, useEffect } from "react";
import { NavLink, useParams, useLocation, Outlet } from "react-router-dom";
import styled from "styled-components";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import api from "../../../utils/apiConfig";
import { useAnnualReview } from "../../../hooks/useAnnualReview";
import SidebarTemplate from "../../../components/shared/SidebarTemplate";
import CallConfirmationModal from "../../../components/domains/advisor-portal/CallConfirmationModal";

// Import existing components from the correct location
import HouseholdInformation from "../../../components/domains/advisor-portal/client-profile/HouseholdInformation";
import CareerEducation from "../../../components/domains/advisor-portal/client-profile/CareerEducation";
import HealthLife from "../../../components/domains/advisor-portal/client-profile/HealthLife";
import IncomeBenefits from "../../../components/domains/advisor-portal/client-profile/IncomeBenefits";
import ExpensesBudgets from "../../../components/domains/advisor-portal/client-profile/ExpensesBudgets";
import BalanceSheet from "../../../components/domains/advisor-portal/client-profile/BalanceSheet";
import GoalsLegacy from "../../../components/domains/advisor-portal/client-profile/GoalsLegacy";
import SummaryActionItems from "../../../components/domains/advisor-portal/client-profile/SummaryActionItems";

import logger from '../../../utils/logger';

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    border-radius: 3px;
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error("🚨 ClientProfile Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-red-600">
          <h2>Something went wrong.</h2>
          <pre>{this.state.error?.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const ClientProfile = () => {
  const { householdId, userId } = useParams();
  const location = useLocation();

  // Replace old sidebar state with unified state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [transcript, setTranscript] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [notesSummary, setNotesSummary] = useState("");
  const [notesUpdates, setNotesUpdates] = useState([]);
  const [notesTasks, setNotesTasks] = useState([]);
  const [showCallModal, setShowCallModal] = useState(false);

  // Keep existing state
  const [clientName, setClientName] = useState("Client");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debtsData, setDebtsData] = useState([]);
  const [members, setMembers] = useState([]);
  const [assetsData, setAssetsData] = useState([]);
  const [assetTypesData, setAssetTypesData] = useState([]);
  const [taxQualificationsData, setTaxQualificationsData] = useState([]);
  const [accountTypesData, setAccountTypesData] = useState([]);
  const [financialInstitutions, setFinancialInstitutions] = useState([]);

  // Keep the annual review hook
  const { annualReviewData, loading: annualReviewLoading } =
    useAnnualReview(householdId);

  // Add useEffect to fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}`,
        );

        if (response.data?.name) {
          setClientName(response.data.name);
        }
        setLoading(false);
      } catch (error) {
        logger.error("Error fetching client data:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchClientData();
  }, [householdId, userId]);

  // Update assets data when annual review data changes
  useEffect(() => {
    if (annualReviewData?.assets) {
      setAssetsData(annualReviewData.assets);
      if (annualReviewData.assetTypes)
        setAssetTypesData(annualReviewData.assetTypes);
      if (annualReviewData.taxQualifications)
        setTaxQualificationsData(annualReviewData.taxQualifications);
      if (annualReviewData.accountTypes)
        setAccountTypesData(annualReviewData.accountTypes);
      if (annualReviewData.financialInstitutions)
        setFinancialInstitutions(annualReviewData.financialInstitutions);
    }
  }, [annualReviewData]);

  // Update debts data when annual review data changes
  useEffect(() => {
    if (annualReviewData?.debts) {
      setDebtsData(annualReviewData.debts);
    }
  }, [annualReviewData]);

  // Update members data when annual review data changes
  useEffect(() => {
    if (annualReviewData?.members) {
      setMembers(annualReviewData.members);
    }
  }, [annualReviewData]);

  // Add function to save updated assets
  const handleSaveAssets = async (updatedAssets) => {
    try {
      const response = await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/assets`,
        { assets: updatedAssets },
      );

      setAssetsData(response.data.assets);
      return true;
    } catch (error) {
      logger.error("Error saving assets:", error);
      return false;
    }
  };

  // Add function to save updated debts
  const handleSaveDebts = async (updatedDebts) => {
    try {
      const response = await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/debts`,
        { debts: updatedDebts },
      );

      setDebtsData(response.data.debts);
      return true;
    } catch (error) {
      logger.error("Error saving debts:", error);
      return false;
    }
  };

  // Add debugging logs
  logger.info("🏠 ClientProfile - Component Mounted");
  logger.info("📍 Current Location:", location);
  logger.info("🔑 Params:", { householdId, userId });

  const navigation = [
    { name: "Household Information", path: "household-information" },
    { name: "Career & Education", path: "career-education" },
    { name: "Health & Life", path: "health-life" },
    { name: "Income & Benefits", path: "income-benefits" },
    { name: "Expenses & Budgets", path: "expenses-budgets" },
    { name: "Balance Sheet", path: "balance-sheet" },
    { name: "Goals & Legacy", path: "goals-legacy" },
    { name: "Summary & Action Items", path: "summary-action-items" },
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const handleAddTranscriptLine = (line) => {
    setTranscript((prev) => [...prev, line]);
  };

  const handleResetTranscript = () => {
    setTranscript([]);
    setIsPaused(false);
    setIsRecording(false);
  };

  const handleHighlightSummary = (summary) => {
    setNotesSummary((prev) => prev + "\n\n" + summary);
  };

  const handleCallConfirm = () => {
    setShowCallModal(false);
    setIsRecording(true);
    setSidebarCollapsed(false);
  };

  const breadcrumbItems = [
    { label: "Advisor Portal", path: "/advisor-portal" },
    { label: "Client Database", path: "/advisor-portal/client-database" },
    {
      label: "Household Profile",
      path: `/advisor-portal/client-database/household-profile/${householdId}`,
    },
    {
      label: "Client Profile",
      path: `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}`,
    },
  ];

  return (
    <ErrorBoundary>
      <SidebarTemplate
        isExpanded={!sidebarCollapsed}
        onToggle={handleSidebarToggle}
        disabled={false}
        transcript={transcript}
        onAddTranscriptLine={handleAddTranscriptLine}
        onResetTranscript={handleResetTranscript}
        isRecording={isRecording}
        setIsRecording={setIsRecording}
        isPaused={isPaused}
        setIsPaused={setIsPaused}
        onHighlightSummary={handleHighlightSummary}
        notesSummary={notesSummary}
        onNotesSummaryChange={setNotesSummary}
        notesUpdates={notesUpdates}
        onNotesUpdatesChange={setNotesUpdates}
        notesTasks={notesTasks}
        onNotesTasksChange={setNotesTasks}
      />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          transition: "margin-right 0.3s cubic-bezier(0.4,0,0.2,1)",
          marginRight: 0,
        }}
      >
        <SubPageLayout
          title={`Client Profile - ${clientName}`}
          breadcrumbItems={breadcrumbItems}
          loading={loading}
          error={error}
        >
          <ContentContainer>
            {/* Navigation Tabs */}
            <nav className="mb-6">
              <div className="flex justify-evenly px-2 overflow-x-auto">
                {navigation.map((item) => (
                  <NavLink
                    key={item.path}
                    to={`/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/${item.path}`}
                    end
                    className={({ isActive }) =>
                      `flex-grow text-center whitespace-nowrap py-2 px-3 rounded-t-lg font-medium text-sm transition-colors ${
                        isActive
                          ? "bg-[#1a237e] text-white"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>
            </nav>

            {/* Content Area */}
            <div className="flex-1 min-h-0 overflow-auto">
              <div className="bg-surface-default dark:bg-surface-default shadow rounded-lg p-6">
                <Outlet
                  context={{
                    householdId,
                    userId,
                    debtsData,
                    members,
                    onSaveDebts: handleSaveDebts,
                    assetsData,
                    assetTypesData,
                    taxQualificationsData,
                    accountTypesData,
                    financialInstitutions,
                    onSaveAssets: handleSaveAssets,
                  }}
                />
              </div>
            </div>
          </ContentContainer>
        </SubPageLayout>
      </div>

      {/* Call Modal Portal */}
      {showCallModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <CallConfirmationModal
            isOpen={showCallModal}
            onClose={() => setShowCallModal(false)}
            onConfirm={handleCallConfirm}
            client={{
              firstName: "Brenda",
              lastName: "Smith",
              name: "Brenda Smith",
              contact: {
                primary_number: "(555) 123-4567",
                phoneType: "Mobile",
              },
            }}
          />
        </div>
      )}
    </ErrorBoundary>
  );
};

export default ClientProfile;
